import { resolveResumeStep, type OnboardingStep } from './steps';
import { STEP_ROUTES, type FounderStep, type InvestorStep } from './machine';

type Role = 'startup' | 'investor';

export type RouteInput = {
  hasAuth: boolean;
  role: Role | null;
  mayEnter: boolean;
  isMockMode: boolean;
  hasProfile: boolean;
  onboardingStep: string | null;
  isInvitee: boolean;
  /**
   * Task 4 — the founder already resolves to an established startup, via an
   * ACTIVE `team_members` row OR legacy `startup_profiles.user_id` ownership
   * (see `resolveMyStartup` in `src/lib/startup/use-my-startup.ts`). A returning
   * owner must be surfaced into their workspace, NOT re-onboarded into the
   * "create a company" flow. Optional (defaults falsy) so the many existing
   * callers/tests that pre-date this signal are unaffected — an omitted value
   * preserves the prior create-flow routing exactly.
   */
  ownsExistingStartup?: boolean;
};

// The founder workspace entry — where an established-startup owner is surfaced
// instead of the onboarding "create a company" flow (Task 4). Kept in sync with
// the dashboard redirect in `src/app/index.tsx`.
const STARTUP_DASHBOARD = '/(startup)/startup-dashboard';

// Onboarding routes. The four founder CORE steps (personal/company/essentials/
// data-source) are sourced from the canonical machine (`STEP_ROUTES.founder`,
// with 'data-source' aliased to the machine's 'activation') so routes are not
// duplicated. The legacy `welcome` bookend was removed in the founder-flow
// reshape (Task 3) — a fresh founder resolves straight to `personal`. Only the
// shared `complete` result screen remains outside the machine. The investor
// path routes through the machine's 2-step identity→thesis flow
// (`STEP_ROUTES.investor`, Task 6).
const LEGACY_TO_FOUNDER_STEP: Partial<Record<OnboardingStep, FounderStep>> = {
  personal: 'personal',
  company: 'company',
  essentials: 'essentials',
  'data-source': 'activation',
};

// The shared completion screen used by both roles.
const SHARED_ROUTES: Record<string, string> = {
  complete: '/(onboarding)/complete',
};

/**
 * Map the raw persisted `onboarding_step` pointer to an investor machine step
 * (Task 6). Investor onboarding is the 2-step identity→thesis flow; the pointer
 * `thesis` (written by the identity screen) resumes at thesis, everything else
 * enters at identity. Screens prefill from the store/DB, so resuming at identity
 * is safe even mid-flow.
 */
function investorResumeStep(persisted: string | null | undefined): InvestorStep | 'complete' {
  if (persisted === 'thesis') return 'thesis';
  if (persisted === 'complete') return 'complete';
  return 'identity';
}

export function routeForStep(role: Role, step: OnboardingStep): string {
  if (role === 'startup') {
    const founderStep = LEGACY_TO_FOUNDER_STEP[step];
    if (founderStep) return STEP_ROUTES.founder[founderStep];
    return SHARED_ROUTES[step] ?? `/(onboarding)/startup-${step}`;
  }
  // Investor: the resume step passed here is founder-shaped (banner-progress
  // uses the founder list), so collapse it to the 2-step machine flow. 'complete'
  // is the shared bookend; everything else enters at identity. The finer
  // identity↔thesis resume is driven from the raw pointer in resolveOnboardingRoute.
  if (step === 'complete') return SHARED_ROUTES.complete;
  return STEP_ROUTES.investor.identity;
}

export function resolveOnboardingRoute(input: RouteInput): string | null {
  if (!input.hasAuth || !input.role || input.mayEnter) return null;
  if (!input.isMockMode && !input.hasProfile) return null;

  if (input.role === 'investor') {
    const step = investorResumeStep(input.onboardingStep);
    if (step === 'complete') return SHARED_ROUTES.complete;
    return STEP_ROUTES.investor[step];
  }

  const step = resolveResumeStep(input.onboardingStep, input.isInvitee);

  // Existing-startup founder (Task 4): a founder who already owns an established
  // startup — active `team_members` membership OR legacy `startup_profiles
  // .user_id` ownership the backfill migration now covers — must be surfaced
  // into their workspace, not dropped into "create a company". This fires for
  // the two pre-establishment steps: `personal` (identity — the legacy
  // null-pointer case, e.g. smoke@) AND `company` (find/create/claim). Both are
  // moot for someone who already owns a company; in particular, being parked at
  // the `company` step while owning a startup is inherently contradictory
  // (creating a company advances you PAST that step), so it's always a
  // stale/legacy pointer, never a genuine unfinished step. The DATA steps
  // (`essentials`/`data-source`) still resume below, so an owner who legitimately
  // hasn't finished those is never skipped. Invitees are excluded — their
  // personal step is genuinely required (`!isInvitee` guard is load-bearing).
  if (input.ownsExistingStartup && !input.isInvitee && (step === 'personal' || step === 'company')) {
    return STARTUP_DASHBOARD;
  }

  return routeForStep('startup', step);
}
